import {
  Component,
  Input,
  AfterViewInit,
  OnChanges,
  OnDestroy,
  ViewChild,
  ElementRef,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  Filler,
} from 'chart.js';
import { ChartComponent } from '../chart/chart.component';
import { LineChartData } from '../../interfaces/global/chart';
import { DictionaryService } from '../../services/dictionary.service';

@Component({
  selector: 'app-line-chart',
  imports: [CommonModule],
  templateUrl: './line-chart.component.html',
  styleUrl: './line-chart.component.scss',
})
export class LineChartComponent
  extends ChartComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  @Input() data: LineChartData = { labels: [], datasets: [] };
  @ViewChild('linechartcanvas', { static: true }) public chartElm?: ElementRef;

  private dictionaryService = inject(DictionaryService);

  private lineColor = '#BFC1C3';
  private pointColor = '#2BB5CF';
  private pointHoverColor = '#1d8fa6';

  constructor() {
    super();
    this.chartType = 'line' as any;
    Chart.register(LineController, LineElement, PointElement, Filler);
  }

  ngAfterViewInit(): void {
    if (this.chartElm) {
      this.chart = new Chart(this.chartElm.nativeElement, {
        type: 'line',
        data: { labels: [], datasets: [] },
        options: this.getBaseOptions(),
      });

      this.applyChartState();
    }
  }

  override ngOnChanges(): void {
    this.applyChartState();
  }

  private applyChartState(): void {
    if (!this.chart) return;

    if (this.isLoading) {
      this.setLoadingOptions();
    } else if (this.isDataAvailable()) {
      this.setDataOptions();
    } else {
      this.setEmptyOptions();
    }

    this.chart.update();
  }

  private getBaseOptions(): any {
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index' as const,
        intersect: false,
      },
      plugins: {
        legend: {
          display: true,
          position: 'top' as const,
          labels: {
            usePointStyle: true,
            pointStyle: 'rect',
            font: { size: 12 },
          },
        },
        datalabels: { display: false },
        tooltip: {
          backgroundColor: '#2a2a2a',
          titleColor: '#fff',
          bodyColor: '#fff',
          padding: 12,
          cornerRadius: 8,
          displayColors: true,
          boxPadding: 4,
          callbacks: {
            title: (items: any[]) => items[0]?.label || '',
          },
        },
      },
      scales: {
        x: {
          grid: { display: true, color: 'rgba(0,0,0,0.05)' },
          ticks: {
            maxRotation: 45,
            minRotation: 45,
            font: { size: 11 },
            color: '#6b7280',
          },
        },
        y: {
          grid: { display: true, color: 'rgba(0,0,0,0.05)' },
          ticks: {
            stepSize: 1,
            font: { size: 11 },
            color: '#6b7280',
          },
          beginAtZero: false,
        },
      },
    };
  }

  override setLoadingOptions(): void {
    if (!this.chart) return;

    const loadingLabels = Array.from({ length: 10 }, (_, i) => `--`);
    const loadingData = Array.from({ length: 10 }, () =>
      Math.floor(Math.random() * 10 + 1),
    );

    this.chart.data = {
      labels: loadingLabels,
      datasets: [
        {
          label: '...',
          data: loadingData,
          borderColor: '#e5e7eb',
          backgroundColor: '#e5e7eb',
          pointBackgroundColor: '#d1d5db',
          pointBorderColor: '#d1d5db',
          pointRadius: 4,
          tension: 0,
        },
      ],
    };

    this.chart.options = {
      ...this.getBaseOptions(),
      plugins: {
        ...this.getBaseOptions().plugins,
        legend: { display: false },
        tooltip: { enabled: false },
      },
      animation: { duration: 0 },
    };
  }

  override setDataOptions(): void {
    if (!this.chart) return;

    const datasets = this.data.datasets.map((ds, index) => ({
      label: ds.label,
      data: ds.values,
      borderColor: this.lineColor,
      backgroundColor: this.pointColor,
      pointBackgroundColor: this.pointColor,
      pointBorderColor: this.pointColor,
      pointHoverBackgroundColor: this.pointHoverColor,
      pointHoverBorderColor: this.pointHoverColor,
      pointRadius: 5,
      pointHoverRadius: 7,
      borderWidth: 2,
      tension: 0,
      fill: false,
    }));

    this.chart.data = {
      labels: this.data.labels,
      datasets,
    };

    this.chart.options = this.getBaseOptions();
  }

  override setEmptyOptions(): void {
    if (!this.chart) return;

    this.chart.data = {
      labels: ['No Data'],
      datasets: [
        {
          label: 'No Data',
          data: [0],
          borderColor: '#e5e7eb',
          backgroundColor: '#e5e7eb',
          pointRadius: 0,
        },
      ],
    };

    this.chart.options = {
      ...this.getBaseOptions(),
      plugins: {
        ...this.getBaseOptions().plugins,
        legend: { display: false },
        tooltip: { enabled: false },
      },
    };
  }

  override isDataAvailable(): boolean {
    return (
      this.data &&
      this.data.labels.length > 0 &&
      this.data.datasets.length > 0 &&
      this.data.datasets.some((ds) => ds.values.length > 0)
    );
  }
}
